import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import React from "react";
import {
  Check,
  CheckOutlined,
  ErrorOutline,
  InfoOutlined,
} from "@mui/icons-material";
import {
  ACTIVE_GREEN,
  DM_COLOR_ACCENT,
  INACTIVE_RED,
  INFO_BLUE,
} from "src/styles";
import useFirmwareUpdateContext from "./FirmwareUpdate/FirmwareUpdateContext";
import { AlenFirmwareUpdateStatus } from "./FirmwareUpdate/types";

const StatusIcon = ({ status }: { status: AlenFirmwareUpdateStatus }) => {
  let statusColor: string;
  switch (status) {
    case AlenFirmwareUpdateStatus.NoUpdate:
      statusColor = ACTIVE_GREEN;
      break;
    case AlenFirmwareUpdateStatus.UpdateAvailable:
      statusColor = INFO_BLUE;
      break;
    case AlenFirmwareUpdateStatus.UpdateError:
      statusColor = INACTIVE_RED;
      break;
    default:
      statusColor = DM_COLOR_ACCENT;
      break;
  }
  let icon;
  switch (status) {
    case AlenFirmwareUpdateStatus.NoUpdate:
      icon = CheckOutlined;
      break;
    case AlenFirmwareUpdateStatus.UpdateAvailable:
      icon = InfoOutlined;
      break;
    case AlenFirmwareUpdateStatus.UpdateError:
      icon = ErrorOutline;
      break;
    default:
      icon = Check;
      break;
  }
  return (
    <Box
      sx={(theme) => ({
        backgroundColor: statusColor,
        padding: theme.spacing(1),
        width: theme.spacing(6),
        height: theme.spacing(6),
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      })}
    >
      {React.createElement(icon, {
        sx: {
          color: "white",
        },
      })}
    </Box>
  );
};

export default function CheckFirmwareUpdateModal() {
  const {
    selectedDeviceFirmware,
    setSelectedDeviceFirmware,
    startFirmwareUpdate,
    updateProgress,
  } = useFirmwareUpdateContext();
  if (selectedDeviceFirmware === null) {
    return null;
  }
  const { firmware, device, error } = selectedDeviceFirmware;
  return (
    <Dialog
      open={selectedDeviceFirmware !== null && updateProgress === null}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>Firmware Update</DialogTitle>
      <DialogContent>
        <Stack gap={2} direction="column">
          <Typography variant="subtitle1">{device.name}</Typography>
          {firmware === null ? (
            <>
              {error ? (
                <Stack direction="row" gap={2} alignItems="center">
                  <Typography variant="body1">
                    Error checking firmware update status
                  </Typography>
                  <StatusIcon status={AlenFirmwareUpdateStatus.UpdateError} />
                </Stack>
              ) : (
                <Typography variant="body1">
                  Checking firmware update status...
                </Typography>
              )}
            </>
          ) : (
            <>
              {firmware.upgrade_status ===
                AlenFirmwareUpdateStatus.NoUpdate && (
                <Stack direction="row" gap={2} alignItems="center">
                  <Typography variant="body1">
                    Firmware({firmware.current_version}) is already up to date.
                    No update available
                  </Typography>
                  <StatusIcon status={firmware.upgrade_status} />
                </Stack>
              )}
              {firmware.upgrade_status ===
                AlenFirmwareUpdateStatus.UpdateAvailable && (
                <Stack direction="row" gap={2} alignItems="center">
                  <Typography variant="body1">
                    New firmware is available. Do you want to update?
                  </Typography>
                  <StatusIcon status={firmware.upgrade_status} />
                </Stack>
              )}
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Stack direction="row" gap={2}>
          {firmware?.upgrade_status ===
            AlenFirmwareUpdateStatus.UpdateAvailable && (
            <Button
              onClick={() => {
                startFirmwareUpdate();
              }}
              variant="contained"
              color="primary"
            >
              Update
            </Button>
          )}
          <Button
            onClick={() => setSelectedDeviceFirmware(null)}
            color="danger"
            variant="outlined"
          >
            Close
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
